"use client";

import React, { useState, useEffect, useRef } from "react";
import { Link, usePathname, useRouter } from "@/i18n/routing";
import { cn } from "@/lib/utils";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import AccessButton from "./access-button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "./ui/navigation-menu";

import { Sheet, SheetClose, SheetContent, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";
import { MenuIcon, Users, ChevronDown, NotepadText } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import ToggleLanguage from "./toggle-language";
import RoleToggle from "./role-toggle";
import { useUserStore } from "@/store/store";
import { BarbellsIcon, ChartIcon, DartIcon, TrainerIcon } from "./icons";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import { GymReportIcon } from "./icons/gym-report";
import { BankCard } from "./icons/bank-card";
import { SaladIcon } from "./icons/salad";

const navLinks = ["home", "features", "pricing", "blog", "contact_us"];
const linkRoutes = navLinks.map((link) => link.replace("_", "-"));
const groupNames = navLinks.map((link) => link.split("_")[0]);

const Navbar = () => {
  const pathname = usePathname();
  const locale = useLocale();
  const router = useRouter();
  const { role } = useUserStore();
  const t = useTranslations("general");
  
  // State for mobile menu
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Close mobile menu when pathname changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Function to handle navigation and close mobile menu
  const handleNavigation = () => {
    setIsMobileMenuOpen(false);
  };

  const trainerFeatures = [
    {
      name: t("trainer.client_management"),
      path: "/trainer/client-management",
      icon: BarbellsIcon,
    },
    {
      name: t("trainer.fitness_planning"),
      path: "/trainer/fitness-planning",
      icon: NotepadText,
    },
    {
      name: t("trainer.progress_tracking"),
      path: "/progress-tracking",
      icon: ChartIcon,
    },
    {
      name: t("trainer.goals_and_habits"),
      path: "/goals-and-habits",
      icon: DartIcon,
    },
    {
      name: t("trainer.payment"),
      path: "/trainer/payment",
      icon: BankCard,
    },
    {
      name: t("trainer.team"),
      path: "/trainer/team",
      icon: Users,
    },
  ];

  const clientFeatures = [
    {
      name: t("client.coaching"),
      path: "/client/coaching",
      icon: TrainerIcon,
    },
    {
      name: t("client.workout_planning"),
      path: "/client/workout",
      icon: GymReportIcon,
    },
    {
      name: t("client.nutrition_plans"),
      path: "/client/nutrition",
      icon: SaladIcon,
    },
    {
      name: t("client.goals_and_habits"),
      path: "/goals-and-habits",
      icon: DartIcon,
    },
    {
      name: t("client.progress_tracking"),
      path: "/progress-tracking",
      icon: ChartIcon,
    },
  ];

  // Check if current pathname is a feature page
  const isFeaturePage = () => {
    const allFeaturePaths = [...trainerFeatures, ...clientFeatures].map(feature => feature.path);
    return allFeaturePaths.some(path => 
      pathname === path || pathname.startsWith(`${path}/`)
    );
  };

  // Check if specific feature is active
  const isFeatureActive = (featurePath: string) => {
    return pathname === featurePath || pathname.startsWith(`${featurePath}/`);
  };

  const getNavHref = (link: string, route: string) => {
    if (link === "home") {
      return "/";
    }

    if (link === "pricing") {
      return `/${role}/pricing`;
    }

    if (link === "blog") {
      return "/blog";
    }

    return `/${route}`;
  };

  const isNavActive = (link: string, route: string) => {
    if (link === "home") {
      return pathname === "/";
    }

    if (link === "pricing") {
      return pathname.includes("/pricing");
    }

    if (link === "blog") {
      return pathname.includes("/blog");
    }

    return (
      pathname.includes(`/${route}`) ||
      pathname.includes(`/${locale}/${route}`)
    );
  };

  return (
    <nav className="fixed z-50 w-full px-4 md:px-12 bg-black-bg flex justify-between items-center">
      <div className="flex justify-between items-center basis-full">
        <div className="flex justify-center items-center gap-8">
          <Link href="/">
            <Image
              src="/logo.webp"
              alt="logo"
              width={100}
              height={100}
              className="py-4 w-24 "
            />
          </Link>
          <NavigationMenu className="hidden static lg:flex ">
            <NavigationMenuList
              className={cn("gap-4", {
                "flex-row-reverse": locale === "ar",
              })}
            >
              {navLinks.map((link, index) =>
                link === "features" ? (
                  <NavigationMenuItem
                    className={`w-full group/${groupNames[index]} relative bg-black-bg py-4`}
                    key={link}
                  >
                    <NavigationMenuTrigger
                      className={cn(
                        `peer/${groupNames[index]} px-2 bg-black-bg text-primary-foreground font-poppins data-[active]:bg-transparent data-[state=open]:bg-transparent hover:bg-transparent focus:bg-transparent focus:text-primary-foreground text-white [&>svg]:text-white [&>svg]:hover:text-white`,
                        {
                          "text-transparent bg-gradient-to-r from-[#8100FF] to-[#323DFB] bg-clip-text": isFeaturePage(),
                        }
                      )}
                    >
                      <span className={cn(
                        "text-white hover:text-transparent hover:bg-gradient-to-r hover:from-[#8100FF] hover:to-[#323DFB] hover:bg-clip-text transition-all",
                        {
                          "text-transparent bg-gradient-to-r from-[#8100FF] to-[#323DFB] bg-clip-text": isFeaturePage(),
                        }
                      )}>
                        {t("features")}
                      </span>
                    </NavigationMenuTrigger>
                    <span
                      className={cn(
                        `absolute w-full h-1 3xl:h-2 bg-gradient-to-r from-primary to-second-primary bottom-0 left-0 rounded-tl-lg rounded-tr-lg opacity-0 transition`,
                        {
                          "opacity-100": isFeaturePage(),
                          "group-hover/features:opacity-50":
                            link === "features",
                          "peer-focus/features:opacity-50": link === "features",
                        }
                      )}
                    ></span>
                    <NavigationMenuContent className="nav-content origin-top backdrop-blur-lg bg-black/75 !w-screen h-[50vh] text-white top-0">
                      <ul className={cn("absolute h-full py-4 flex flex-col gap-3 justify-center", {
                        "left-[20%]": locale === "en",
                        "right-[20%]": locale === "ar",
                      })}>
                        <span className={cn("font-poppins text-gray-300 text-xs font-light", {
                          "text-left": locale === "en",
                          "text-right": locale === "ar",
                        })}>
                          {t("explore_features")}
                        </span>
                                                    {role === "trainer"
                              ? trainerFeatures.map((feature) => (
                              <li className={cn("text-3xl", {
                                "pl-2": locale === "en",
                                "pr-2": locale === "ar",
                              })} key={feature.name}>
                                <NavigationMenuLink
                                  asChild
                                  className={cn("flex items-center gap-2", {
                                    "flex-row": locale === "en",
                                    "flex-row-reverse": locale === "ar",
                                  })}
                                >
                                  <Link 
                                    href={feature.path} 
                                    className={cn(
                                      "text-white hover:text-transparent hover:bg-gradient-to-r hover:from-[#8100FF] hover:to-[#323DFB] hover:bg-clip-text transition-all",
                                      {
                                        "text-transparent bg-gradient-to-r from-[#8100FF] to-[#323DFB] bg-clip-text": isFeatureActive(feature.path),
                                      }
                                    )}
                                  >
                                    <feature.icon
                                      size={24}
                                      className="text-white"
                                    />
                                    <span className={cn(
                                      "text-white hover:text-transparent hover:bg-gradient-to-r hover:from-[#8100FF] hover:to-[#323DFB] hover:bg-clip-text",
                                      {
                                        "text-transparent bg-gradient-to-r from-[#8100FF] to-[#323DFB] bg-clip-text": isFeatureActive(feature.path),
                                      }
                                    )}>
                                      {feature.name}
                                    </span>
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                            ))
                                                        : clientFeatures.map((feature) => (
                              <li className={cn("text-3xl", {
                                "pl-2": locale === "en",
                                "pr-2": locale === "ar",
                              })} key={feature.name}>
                                <NavigationMenuLink
                                  asChild
                                  className={cn("flex items-center gap-2", {
                                    "flex-row": locale === "en",
                                    "flex-row-reverse": locale === "ar",
                                  })}
                                >
                                  <Link 
                                    href={feature.path} 
                                    className={cn(
                                      "text-white hover:text-transparent hover:bg-gradient-to-r hover:from-[#8100FF] hover:to-[#323DFB] hover:bg-clip-text transition-all group",
                                      {
                                        "text-transparent bg-gradient-to-r from-[#8100FF] to-[#323DFB] bg-clip-text": isFeatureActive(feature.path),
                                      }
                                    )}
                                  >
                                    <feature.icon
                                      size={24}
                                      className={feature.name === t("client.coaching") ? "text-white [&_path:last-of-type]:text-black" : "text-white"}
                                    />
                                    <span className={cn(
                                      "text-white group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-[#8100FF] group-hover:to-[#323DFB] group-hover:bg-clip-text",
                                      {
                                        "text-transparent bg-gradient-to-r from-[#8100FF] to-[#323DFB] bg-clip-text": isFeatureActive(feature.path),
                                      }
                                    )}>
                                      {feature.name}
                                    </span>
                                  </Link>
                                </NavigationMenuLink>
                              </li>
                            ))}
                        <span className={cn("font-poppins text-gray-300 text-xs font-light", {
                          "text-left": locale === "en",
                          "text-right": locale === "ar",
                        })}>
                          GRITIZER
                        </span>
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                ) : (
                  <NavigationMenuItem
                    key={link}
                    className={`w-full group/${groupNames[index]} relative bg-black-bg py-4`}
                  >
                    <NavigationMenuLink
                       asChild
                       className={cn(
                         navigationMenuTriggerStyle(),
                         `peer/${groupNames[index]} px-2 bg-black-bg text-primary-foreground font-poppins transition focus:bg-transparent focus:text-primary-foreground hover:bg-transparent active:bg-transparent data-[active]:bg-transparent !bg-transparent hover:!bg-transparent active:!bg-transparent data-[active]:!bg-transparent`
                       )}
                     >
                       <Link
                        href={getNavHref(link, linkRoutes[index])}
                        className={cn(
                          "text-white transition-all hover:text-transparent hover:bg-gradient-to-r hover:from-[#8100FF] hover:to-[#323DFB] hover:bg-clip-text bg-transparent hover:bg-transparent active:bg-transparent !bg-transparent hover:!bg-transparent active:!bg-transparent",
                          {
                            "!text-transparent !bg-gradient-to-r !from-[#8100FF] !to-[#323DFB] !bg-clip-text": 
                              isNavActive(link, linkRoutes[index]),
                          }
                        )}
                      >
                        {t(link)}
                      </Link>
                    </NavigationMenuLink>
                    <span
                      className={cn(
                        `absolute w-full h-1 3xl:h-2 bg-gradient-to-r from-primary to-second-primary bottom-0 left-0 rounded-tl-lg rounded-tr-lg opacity-0 transition`,
                        {
                          "opacity-100": isNavActive(link, linkRoutes[index]),
                           "group-hover/home:opacity-50": link === "home",
                          "group-hover/pricing:opacity-50": link === "pricing",
                          "group-hover/features:opacity-50":
                            link === "features",
                          "group-hover/blog:opacity-50": link === "blog",
                          "group-hover/contact:opacity-50":
                            link === "contact_us",
                          "peer-focus/home:opacity-50": link === "home",
                          "peer-focus/pricing:opacity-50": link === "pricing",
                          "peer-focus/features:opacity-50": link === "features",
                          "peer-focus/blog:opacity-50": link === "blog",
                          "peer-focus/contact:opacity-50":
                            link === "contact_us",
                        }
                      )}
                    ></span>
                  </NavigationMenuItem>
                )
              )}
            </NavigationMenuList>
          </NavigationMenu>
        </div>
        <div className="flex justify-center items-center gap-2 md:gap-4 relative">
          <div className="hidden lg:block">
            <ToggleLanguage />
          </div>
          <RoleToggle className="hidden lg:block" />
          <AccessButton />
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger
              asChild
              className="flex justify-between items-center lg:hidden"
            >
              <Button className="rounded-full px-3 py-4 bg-gradient-to-t from-[#8100FF] to-[#323DFB] border-transparent text-white hover:from-[#323DFB] hover:to-[#8100FF] transition-all">
                <MenuIcon />
              </Button>
            </SheetTrigger>
            <SheetContent
              side={locale === "en" ? "right" : "left"}
              className={cn(
                "bg-black-bg w-72 flex flex-col h-full",
                locale === "ar" && "[&>button]:left-4 [&>button]:right-auto"
              )}
            >
              <div className="flex flex-col flex-shrink-0">
                <RoleToggle className="mb-6" />
                <NavigationMenu className={cn("max-w-full", locale === "ar" ? "justify-end" : "justify-start")}>
                  <NavigationMenuList className={cn("flex-col w-full gap-2 space-x-0 space-y-1", locale === "ar" && "items-end")}>
                  {navLinks.map((link, index) => (
                    <NavigationMenuItem
                      key={link}
                      className={cn("w-full group relative bg-black-bg px-4", locale === "ar" && "text-right")}
                    >
                      {link !== "features" ? (
                        <Link
                          href={getNavHref(link, linkRoutes[index])}
                          onClick={handleNavigation}
                          className={cn(
                            navigationMenuTriggerStyle(),
                            "px-2 w-full bg-black-bg text-primary-foreground font-poppins hover:bg-transparent hover:text-transparent hover:bg-gradient-to-r hover:from-[#8100FF] hover:to-[#323DFB] hover:bg-clip-text transition focus:bg-transparent focus:text-primary-foreground",
                            locale === "ar" ? "justify-end text-right" : "justify-start text-left",
                            {
                              "!text-transparent !bg-gradient-to-r !from-[#8100FF] !to-[#323DFB] !bg-clip-text": 
                                isNavActive(link, linkRoutes[index]),
                            }
                          )}
                        >
                          {t(link)}
                        </Link>
                      ) : (
                        <Collapsible>
                          <CollapsibleTrigger
                            className={cn(
                              "px-2 w-full flex items-center bg-black-bg text-primary-foreground font-poppins hover:bg-transparent focus:bg-transparent focus:text-primary-foreground py-3",
                              locale === "ar" ? "justify-between flex-row-reverse text-right" : "justify-between text-left",
                              {
                                "!text-transparent !bg-gradient-to-r !from-[#8100FF] !to-[#323DFB] !bg-clip-text": isFeaturePage(),
                              }
                            )}
                          >
                            <span className={cn(
                              "text-white hover:text-transparent hover:bg-gradient-to-r hover:from-[#8100FF] hover:to-[#323DFB] hover:bg-clip-text transition-all",
                              locale === "ar" ? "text-right" : "text-left",
                              {
                                "!text-transparent !bg-gradient-to-r !from-[#8100FF] !to-[#323DFB] !bg-clip-text": isFeaturePage(),
                              }
                            )}>{t(link)}</span>
                            <ChevronDown className={cn(
                              "w-4 h-4 transition-transform duration-200 ui-open:rotate-180 flex-shrink-0 text-white",
                              locale === "ar" && "ui-open:rotate-[-180deg]"
                            )} />
                          </CollapsibleTrigger>

                          <CollapsibleContent className="flex flex-col gap-2">
                            {role === "trainer"
                              ? trainerFeatures.map((feature) => (
                                  <Link
                                    key={feature.name}
                                    href={feature.path}
                                    onClick={handleNavigation}
                                    className={cn(
                                      "px-2 w-full bg-black-bg text-primary-foreground font-poppins hover:bg-transparent hover:text-transparent hover:bg-gradient-to-r hover:from-[#8100FF] hover:to-[#323DFB] hover:bg-clip-text transition focus:bg-transparent focus:text-primary-foreground",
                                      locale === "ar" ? "justify-end text-right" : "justify-start text-left",
                                      {
                                        "!text-transparent !bg-gradient-to-r !from-[#8100FF] !to-[#323DFB] !bg-clip-text": isFeatureActive(feature.path),
                                      }
                                    )}
                                  >
                                    {feature.name}
                                  </Link>
                                ))
                              : clientFeatures.map((feature) => (
                                  <Link
                                    key={feature.name}
                                    href={feature.path}
                                    onClick={handleNavigation}
                                    className={cn(
                                      "px-2 w-full bg-black-bg text-primary-foreground font-poppins hover:bg-transparent hover:text-transparent hover:bg-gradient-to-r hover:from-[#8100FF] hover:to-[#323DFB] hover:bg-clip-text transition focus:bg-transparent focus:text-primary-foreground",
                                      locale === "ar" ? "justify-end text-right" : "justify-start text-left",
                                      {
                                        "!text-transparent !bg-gradient-to-r !from-[#8100FF] !to-[#323DFB] !bg-clip-text": isFeatureActive(feature.path),
                                      }
                                    )}
                                  >
                                    {feature.name}
                                  </Link>
                                ))}
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                      <span
                        className={cn(
                          "absolute w-1 h-full bg-gradient-to-r from-primary to-second-primary bottom-0 rounded-tl-lg rounded-tr-lg opacity-0 transition",
                          locale === "ar" ? "right-0" : "left-0",
                          {
                            "opacity-100":
                              link === "home"
                                ? pathname === "/"
                                : link === "features"
                                ? isFeaturePage()
                                : pathname.includes(`/${linkRoutes[index]}`) || pathname.includes(`/${locale}/${linkRoutes[index]}`),
                            "group-hover/home:opacity-50":
                              link === "home" && pathname !== "/",
                            "group-hover/features:opacity-50":
                              link === "features",
                            "group-hover/pricing:opacity-50":
                              link === "pricing" &&
                              pathname !== `/${linkRoutes[index]}`,
                            "group-hover/blog:opacity-50":
                              link === "blog" &&
                              !pathname.includes("/blog"),
                            "group-hover/about:opacity-50":
                              link === "about_us" &&
                              pathname !== `/${linkRoutes[index]}`,
                            "group-hover/contact:opacity-50":
                              link === "contact_us" &&
                              pathname !== `/${linkRoutes[index]}`,
                          }
                        )}
                      ></span>
                    </NavigationMenuItem>
                  ))}
                  </NavigationMenuList>
                </NavigationMenu>
              </div>
              <div className="mt-auto pt-6 border-t border-white/10 flex-shrink-0">
                <ToggleLanguage variant="mobile" />
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
